import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Chip,
  CardMedia,
  Paper,
  IconButton,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { RootState } from '../store/store';
import { buildEventsApiUrl } from '../config/api';
import { getImageSourceLabel, normalizeImageUrls } from '../utils/images';
import { getAuthUserDisplayName, getAuthUserId } from '../utils/authUser';
import WantListSelectionModal from './TradeInterestModal';

interface ItemData {
  id: string;
  name: string;
  description?: string;
  category: string;
  condition: string;
  isCashItem?: boolean;
  cashValueUSD?: number;
  notes?: string;
  images?: Array<{
    url: string;
    uploadedAt?: string;
    source?: string;
  }> | string[];
  bggId?: number;
  createdByUserId: string;
  createdAt?: string;
  status?: string;
}

interface UserData {
  id?: string;
  userID?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  displayName?: string;
}

interface EventData {
  eventID?: string;
  id?: string;
  eventName?: string;
  name?: string;
  venueAddress?: string;
  listingCityName?: string;
  listingStateName?: string;
  location?: string;
}

interface SimpleItem {
  id: string;
  name: string;
  createdByUserId?: string;
  addedByUserId?: string;
  userID?: string;
  category?: string;
  isCashItem?: boolean;
  cashValueUSD?: number;
}

interface SavedWantData {
  id?: string;
  candidateOfferedItemIDs?: string[];
  cashOfferUSD?: number;
  status?: string;
}

const isCashItem = (item?: { isCashItem?: boolean; category?: string } | null) => Boolean(item?.isCashItem || String(item?.category || '').toLowerCase() === 'cash offer');

const ItemDetailsPage: React.FC = () => {
  const { eventId, itemId } = useParams<{ eventId: string; itemId: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [item, setItem] = useState<ItemData | null>(null);
  const [event, setEvent] = useState<EventData | null>(null);
  const [owner, setOwner] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const latestLoadIdRef = useRef(0);
  const [myEventItems, setMyEventItems] = useState<SimpleItem[]>([]);
  const [existingWant, setExistingWant] = useState<SavedWantData | null>(null);
  const [wantContextLoaded, setWantContextLoaded] = useState(false);
  const [interestModalOpen, setInterestModalOpen] = useState(false);

  useEffect(() => {
    const loadId = latestLoadIdRef.current + 1;
    latestLoadIdRef.current = loadId;
    const isStale = () => latestLoadIdRef.current !== loadId;

    const fetchData = async () => {
      if (!eventId || !itemId) {
        setError('Missing event or item ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        setWantContextLoaded(false);

        const authUserId = getAuthUserId(user as any);

        const authHeaders = authUserId ? { 'x-user-id': authUserId } : undefined;

        const [itemResponse, eventResponse] = await Promise.all([
          fetch(buildEventsApiUrl(`/api/items/${itemId}`), authHeaders ? { headers: authHeaders } : undefined),
          fetch(buildEventsApiUrl(`/api/events/${eventId}`), authHeaders ? { headers: authHeaders } : undefined)
        ]);

        if (!itemResponse.ok) {
          throw new Error(`Failed to load item: ${itemResponse.statusText}`);
        }

        if (!eventResponse.ok) {
          throw new Error(`Failed to load event: ${eventResponse.statusText}`);
        }

        const itemResponseData = await itemResponse.json();
        const unwrappedItem = itemResponseData.item || itemResponseData;

        const eventResponseData = await eventResponse.json();
        const unwrappedEvent = eventResponseData.event || eventResponseData;

        if (isStale()) {
          return;
        }

        setItem(unwrappedItem as ItemData);
        setEvent(unwrappedEvent as EventData);

        if (unwrappedItem?.createdByUserId) {
          try {
            const userResponse = await fetch(
              buildEventsApiUrl(`/api/users/${unwrappedItem.createdByUserId}/profile`),
              authHeaders ? { headers: authHeaders } : undefined
            );

            if (userResponse.ok) {
              const userResponseData = await userResponse.json();
              const unwrappedUser = userResponseData.profile || userResponseData.user || userResponseData;
              if (!isStale()) {
                setOwner(unwrappedUser as UserData);
              }
            } else if (!isStale()) {
              setOwner(null);
            }
          } catch {
            if (!isStale()) {
              setOwner(null);
            }
          }
        }

        if (authUserId) {
          try {
            const contextResponse = await fetch(buildEventsApiUrl(`/api/events/${eventId}/items/${itemId}/want-list-context?userId=${authUserId}`), {
              headers: { 'x-user-id': authUserId }
            });

            if (contextResponse.ok) {
              const contextData = await contextResponse.json();
              if (isStale()) {
                return;
              }
              setMyEventItems(Array.isArray(contextData.myEventItems) ? contextData.myEventItems : []);
              setExistingWant(contextData.existingWant || null);
            } else if (!isStale()) {
              setMyEventItems([]);
              setExistingWant(null);
            }
          } catch (detailError) {
            if (!isStale()) {
              console.warn('⚠️ Failed to load event item want-list context:', detailError);
              setMyEventItems([]);
              setExistingWant(null);
            }
          } finally {
            if (!isStale()) {
              setWantContextLoaded(true);
            }
          }
        } else if (!isStale()) {
          setMyEventItems([]);
          setExistingWant(null);
          setWantContextLoaded(true);
        }
      } catch (err) {
        if (!isStale()) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to load item details';
          console.error('❌ Error fetching data:', errorMsg);
          setError(errorMsg);
        }
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [eventId, itemId, user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/events/${eventId}`)}
        >
          Back to Event
        </Button>
      </Container>
    );
  }

  if (!item) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Item not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/events/${eventId}`)}
        >
          Back to Event
        </Button>
      </Container>
    );
  }

  const authUserId = getAuthUserId(user as any);
  const isOwnItem = item.createdByUserId === authUserId;
  const canExpressInterest = Boolean(authUserId && eventId && !isOwnItem);
  const targetIsCashItem = isCashItem(item);
  const canShowWantAction = canExpressInterest && wantContextLoaded;

  const handleOpenOwnerProfile = () => {
    const ownerId = item.createdByUserId || owner?.id || owner?.userID;
    if (!ownerId) return;
    navigate(`/users/${ownerId}`);
  };

  const refreshWantContext = async () => {
    if (!authUserId || !eventId) {
      return;
    }

    const contextResponse = await fetch(buildEventsApiUrl(`/api/events/${eventId}/items/${itemId}/want-list-context?userId=${authUserId}`), {
      headers: { 'x-user-id': authUserId }
    });
    if (contextResponse.ok) {
      const contextData = await contextResponse.json();
      setMyEventItems(Array.isArray(contextData.myEventItems) ? contextData.myEventItems : []);
      setExistingWant(contextData.existingWant || null);
    }
  };

  const handleInterestSaved = async () => {
    if (!authUserId || !eventId) {
      setInterestModalOpen(false);
      return;
    }

    try {
      await refreshWantContext();
    } catch (refreshError) {
      console.warn('⚠️ Failed to refresh want-list state:', refreshError);
    } finally {
      setInterestModalOpen(false);
    }
  };

  const handleCashItemCreated = async () => {
    try {
      await refreshWantContext();
    } catch (refreshError) {
      console.warn('⚠️ Failed to refresh cash item context:', refreshError);
    }
  };

  // Extract image URLs
  const imageUrls = normalizeImageUrls(item.images || []);

  const currentImage = imageUrls[currentImageIndex];
  const currentImageSource = getImageSourceLabel(
    Array.isArray(item.images) ? item.images[currentImageIndex] : null,
    Boolean(item.bggId) && currentImageIndex === 0
  );
  const wantSelectionSignature = `${item.id}:${existingWant?.id || 'none'}:${(existingWant?.candidateOfferedItemIDs || []).slice().sort().join(',')}:${existingWant?.cashOfferUSD || 0}`;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(`/events/${eventId}`)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Item Details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Image Section */}
        {imageUrls.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardMedia
                component="img"
                height="400"
                image={currentImage}
                alt={item.name}
                sx={{ objectFit: 'cover' }}
              />
              <CardContent>
                <Chip
                  label={currentImageSource}
                  size="small"
                  color={currentImageSource.startsWith('BoardGameGeek') ? 'primary' : 'default'}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    {currentImageIndex + 1} / {imageUrls.length}
                  </Typography>
                  {imageUrls.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))}
                        disabled={currentImageIndex === 0}
                      >
                        ← Prev
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          setCurrentImageIndex(Math.min(imageUrls.length - 1, currentImageIndex + 1))
                        }
                        disabled={currentImageIndex === imageUrls.length - 1}
                      >
                        Next →
                      </Button>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Details Section */}
        <Grid item xs={12} md={imageUrls.length > 0 ? 6 : 12}>
          <Card>
            <CardContent>
              {/* Item Name */}
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                {item.name}
              </Typography>

              {/* Owner Information */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Item Owner
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {getAuthUserDisplayName(owner as any) || item.createdByUserId}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                  {isOwnItem ? '(You own this item)' : '(Not your item)'}
                </Typography>

                {!isOwnItem && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<PersonIcon />}
                      onClick={handleOpenOwnerProfile}
                    >
                      View Trader Profile
                    </Button>
                    {canExpressInterest && !wantContextLoaded && (
                      <Button
                        variant="outlined"
                        startIcon={<FavoriteBorderIcon />}
                        disabled
                      >
                        Loading want state...
                      </Button>
                    )}
                    {canShowWantAction && (
                      <Button
                        variant={existingWant?.id ? 'outlined' : 'contained'}
                        startIcon={<FavoriteBorderIcon />}
                        onClick={() => setInterestModalOpen(true)}
                      >
                        {existingWant?.id ? 'Edit Want' : 'Add to Want List'}
                      </Button>
                    )}
                  </Stack>
                )}
              </Paper>

              {/* Category & Condition */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'textSecondary' }}>
                  Category & Condition
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={item.category}
                    variant="outlined"
                    color="primary"
                  />
                  <Chip
                    label={item.condition}
                    variant="outlined"
                    color="secondary"
                  />
                  {targetIsCashItem && (
                    <Chip
                      label={`Cash $${Number(item.cashValueUSD || 0).toFixed(2)}`}
                      variant="filled"
                      color="warning"
                    />
                  )}
                </Box>
              </Box>

              {/* Description */}
              {item.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'textSecondary' }}>
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {item.description}
                  </Typography>
                </Box>
              )}

              {/* Notes */}
              {item.notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'textSecondary' }}>
                    Notes
                  </Typography>
                  <Typography variant="body2">
                    {item.notes}
                  </Typography>
                </Box>
              )}

              {/* Event Information */}
              {event && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'textSecondary' }}>
                    In Event
                  </Typography>
                  <Typography variant="body2">
                    <strong>{event.eventName || 'Unknown Event'}</strong>
                    {(event.venueAddress || event.location) && ` • ${event.venueAddress || event.location}`}
                    {(!event.venueAddress && !event.location && event.listingCityName) && ` • ${event.listingCityName}${event.listingStateName ? ', ' + event.listingStateName : ''}`}
                  </Typography>
                </Box>
              )}

              {/* Back Button */}
              <Box sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(`/events/${eventId}`)}
                  fullWidth
                >
                  Back to Event
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <WantListSelectionModal
        key={wantSelectionSignature}
        open={interestModalOpen}
        onClose={() => setInterestModalOpen(false)}
        onSaved={handleInterestSaved}
        eventId={eventId || ''}
        targetItem={canExpressInterest ? { id: item.id, name: item.name, category: item.category, isCashItem: item.isCashItem, cashValueUSD: item.cashValueUSD } : null}
        myItems={myEventItems.map(eventItem => ({ id: eventItem.id, name: eventItem.name, category: eventItem.category, isCashItem: eventItem.isCashItem, cashValueUSD: eventItem.cashValueUSD }))}
        userId={authUserId || ''}
        onCashItemCreated={handleCashItemCreated}
        existingInterest={existingWant}
      />
    </Container>
  );
};

export default ItemDetailsPage;

